import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { generateBreadcrumbSchema, generateFAQSchema } from '../utils/schema';

// Hard-coded service list — rendered immediately on mount (no API call, no spinner).
// This guarantees the full service list is in the DOM on first paint, which
// modern SEO crawlers (Googlebot, Bingbot with JS) can index reliably.
const SERVICES = [
  { slug: 'rodent-control', name: 'Rodent Control', desc: 'Rat & mouse extermination, inspection, exclusion, and prevention for homes, apartments, and commercial properties across NYC and Long Island.', img: '/images/services/rodent.jpg' },
  { slug: 'cockroach-extermination', name: 'Cockroach Extermination', desc: 'German, American, and Oriental roach control for apartments, homes, restaurants, and food establishments. NYC DOHMH-compliant.', img: '/images/services/cockroach.jpg' },
  { slug: 'bed-bug-removal', name: 'Bed Bug Treatment', desc: 'Bed bug inspection, multi-touch treatment, and follow-up for apartments, homes, and multi-unit buildings across NYC.', img: '/images/services/bedbug.jpg' },
  { slug: 'ant-extermination', name: 'Ant Extermination', desc: 'Carpenter, pavement, pharaoh, and sugar ants. Targeted bait and perimeter treatments for homes, apartments, and businesses.', img: '/images/services/ant.jpg' },
  { slug: 'flea-control', name: 'Flea Control', desc: 'Pet-safe flea elimination for NYC homes with cats and dogs. Multi-stage treatment to break the flea life cycle.', img: '/images/services/flea.jpg' },
  { slug: 'wasp-removal', name: 'Wasp Removal', desc: 'Safe wasp, hornet, and bee nest removal from yards, attics, soffits, walls, and eaves. Same-day service available.', img: '/images/services/wasp.jpg' },
  { slug: 'termite-control', name: 'Termite Control', desc: 'WDIR termite inspections, liquid termiticide treatments, and bait systems for homes and businesses across NYC and Long Island.', img: '/images/services/termite.jpg' },
  { slug: 'general-pest-control', name: 'General Pest Control', desc: 'Quarterly and one-time pest control for spiders, silverfish, moths, flies, crickets, and seasonal occasional invaders.', img: '/images/services/general.jpg' },
];

const FAQS = [
  { question: 'What pest control services do you offer in NYC?', answer: 'Reclaim Pest Control Inc offers rodent control, cockroach extermination, bed bug treatment, ant extermination, flea control, wasp removal, termite control, and general pest control across NYC and Long Island.' },
  { question: 'Do you serve apartments and multi-unit buildings?', answer: 'Yes. We treat apartments, condos, co-ops, and multi-unit buildings throughout NYC, coordinating with tenants, landlords, and property managers.' },
  { question: 'How fast can you come for pest control?', answer: 'Same-day pest control service is usually available across most of NYC and Long Island. Call 646-209-9955 to schedule.' },
  { question: 'Are your treatments safe for kids and pets?', answer: 'Yes. We use targeted, pet-safe and family-safe treatments. Our technicians explain placement and precautions during every visit.' },
];

const Services = () => {
  const schemas = [
    generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }]),
    generateFAQSchema(FAQS),
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: SERVICES.map((s, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://www.reclaimpestcontrol.com/services/${s.slug}`,
        name: s.name,
      })),
    },
  ];

  return (
    <div>
      <SEO
        title="Pest Control Services NYC"
        description="Reclaim Pest Control Inc provides pest control services in NYC and Long Island for roaches, rats, mice, bed bugs, ants, fleas, wasps, termites, and general pest problems."
        keywords="pest control services NYC, exterminator services NYC, rodent control, bed bug removal, cockroach extermination, termite control, ant removal, wasp removal, flea control"
        schema={schemas}
        canonical="https://www.reclaimpestcontrol.com/services"
      />

      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <nav className="text-sm mb-4 text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2">/</span>
            <span>Services</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="services-h1">
            Pest Control Services in NYC
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-white/90" data-testid="services-hero-sub">
            Reclaim Pest Control Inc provides professional pest control services for roaches, rats,
            mice, bed bugs, ants, fleas, wasps, termites, and general pest issues across NYC and Long Island.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:6462099955"
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition inline-flex items-center justify-center"
              data-testid="services-hero-call"
            >
              Call 646-209-9955
            </a>
            <Link
              to="/contact"
              className="bg-white text-green-700 hover:bg-gray-100 px-7 py-4 rounded-lg font-bold text-base md:text-lg shadow-lg transition inline-flex items-center justify-center border-2 border-white"
              data-testid="services-hero-quote"
            >
              Request a Pest Inspection
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-2">Our NYC Pest Control Services</h2>
          <p className="text-gray-600 text-center mb-10 max-w-3xl mx-auto">
            Licensed, insured, and NYS DEC-certified. Same-day service available across NYC and Long Island.
            Click any service below for full details, treatment process, and pricing information.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-grid">
            {SERVICES.map((service) => (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition transform hover:-translate-y-1 border border-gray-100"
                data-testid={`service-card-${service.slug}`}
              >
                <img
                  src={service.img}
                  alt={`${service.name} - Reclaim Pest Control NYC`}
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{service.name}</h3>
                  <p className="text-gray-600 mb-4">{service.desc}</p>
                  <span className="text-green-600 font-semibold">Learn More →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              NYC Pest Control Services — Frequently Asked Questions
            </h2>
            <div className="space-y-3" data-testid="services-faq-list">
              {FAQS.map((faq, idx) => (
                <details
                  key={idx}
                  className="bg-gray-50 border border-gray-200 rounded-lg group"
                  data-testid={`services-faq-${idx}`}
                >
                  <summary className="cursor-pointer px-5 py-4 font-semibold text-gray-900 hover:bg-gray-100 transition list-none flex items-center justify-between">
                    <span>{faq.question}</span>
                    <span className="text-green-600 text-xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-700 border-t border-gray-200">
                    <p className="pt-3">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4" data-testid="services-cta-heading">
            Call 646-209-9955 for pest control service in NYC.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Fast, professional, licensed exterminators serving every NYC borough and Long Island.
            Same-day service available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:6462099955"
              className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition"
              data-testid="services-cta-call"
            >
              Call (646) 209-9955
            </a>
            <Link
              to="/contact"
              className="bg-white text-green-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
              data-testid="services-cta-quote"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
